import React, { useEffect, useRef, useMemo } from 'react';
import Card from 'src/components/common/Card.redesign';
import BillingAndPaymentCycle from 'src/models/BillingAndPaymentCycle';
import Landman from 'src/models/Landman';
import ProjectAdministratorStaff from 'src/models/ProjectAdministratorStaff';
import ProjectStaff from 'src/models/ProjectStaff';
import User from 'src/models/User';
import { ContextMenuAction } from 'src/components/common/ContextMenu';
import ProjectWorkClassification from 'src/models/ProjectWorkClassification';
import moment from 'moment';

interface IStaffingLandmanCard {
    projectStaffInfo: ProjectStaff;
    landman: Landman;
    index: number;
    userList: User[];
    projectAdminStaff: ProjectAdministratorStaff[];
    /** @deprecated Use onRegisterSubmit instead — kept for compatibility during migration */
    submitListener?: boolean;
    /** Called on mount so the parent can trigger this card's submit imperatively (zero re-render) */
    onRegisterSubmit?: (fn: () => void) => void;
    /** Called on unmount to clean up the registered handler */
    onUnregisterSubmit?: () => void;
    billingCycles: BillingAndPaymentCycle[];
    workClassifications: ProjectWorkClassification[];
    submitStaffingForm: (data: ProjectStaff) => void;
    showDialog?: ContextMenuAction[];
    hideDialog?: () => void;
    defaultBillRate?: number;
    defaultPayRate?: number;
    defaultPM?: number;
    defaultPCAPM?: number;
}

const currentMonth = moment(new Date()).month() + 1;

// Stable style objects at module level — prevents new object references on every render
const SLC_BODY_FLEX: React.CSSProperties = { display: 'flex', marginBottom: '1rem' };
const SLC_FULL_WIDTH: React.CSSProperties = { width: '100%' };
const SLC_NOWRAP_FULL_WIDTH: React.CSSProperties = { whiteSpace: 'nowrap', width: '100%' };
const SLC_NOWRAP: React.CSSProperties = { whiteSpace: 'nowrap' };
const SLC_CARD_BODY: React.CSSProperties = { display: 'flex', justifyContent: 'left', alignItems: 'left', margin: '0 3rem 0 3rem', gap: '3%' };
const SLC_CHECKBOX_ROW: React.CSSProperties = { whiteSpace: 'nowrap', display: 'flex', gap: '0.5rem', marginBottom: '1rem', width: '300px' };
const SLC_LABEL_NOWRAP: React.CSSProperties = { whiteSpace: 'nowrap' };
// const SLC_RED_STAR: React.CSSProperties = { color: 'red' };

const StaffingLandmanCard = (props: IStaffingLandmanCard) => {
    // Plain ref to the form DOM node — zero react-hook-form overhead.
    // We read values with FormData on submit instead of registering each field.
    const formRef = useRef<HTMLFormElement>(null);

    // Register this card's submitForm() with the parent panel so the Submit button
    // can call all cards imperatively without any state change (zero re-renders on click).
    useEffect(() => {
        props.onRegisterSubmit?.(submitForm);
        return () => { props.onUnregisterSubmit?.(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const currentBillCycle = () => {
        return props.billingCycles.find((bc) => moment(bc.startDate).month() + 1 === currentMonth);
    };

    const billingCycleOptions = useMemo(() => {
        if (!props.billingCycles) { return []; }
        // The API returns massively duplicated records (17k+ with only ~120 unique labels).
        // Deduplicate by label before rendering — drops DOM node count from 17k to ~120.
        const seen = new Set<string>();
        const result: { value: number; label: string }[] = [];
        for (const c of props.billingCycles) {
            const year = new Date(c?.endDate).getFullYear();
            const label = c.cycleNumber <= 9 ? `${year} - 0${c.cycleNumber}` : `${year} - ${c.cycleNumber}`;
            if (!seen.has(label)) {
                seen.add(label);
                result.push({ value: c.id, label });
            }
        }
        return result;
    }, [props.billingCycles]);

    const pcapmOptions = useMemo(() => {
        if (!props.userList) { return []; }
        return props.userList
            .filter((u) => u?.role?.id === 3)
            .map((u) => ({ value: u.id, label: `${u.firstName} ${u.lastName}` }));
    }, [props.userList]);

    const workClassificationOptions = useMemo(() => {
        if (!props.workClassifications) { return []; }
        return props.workClassifications.map((wc) => ({ value: wc.id, label: `${wc.alias} - ${wc.name}` }));
    }, [props.workClassifications]);

    const projectManagerOptions = useMemo(() => {
        if (!props.userList) { return []; }
        return props.userList
            .filter((u) => u?.role?.id === 2)
            .map((u) => ({ value: u.id, label: `${u.firstName} ${u.lastName}` }));
    }, [props.userList]);

    const submitForm = () => {
        if (!formRef.current) { return; }

        const data = new FormData(formRef.current);
        const defaultCycle = currentBillCycle();

        let startBillingCycleId = parseInt(data.get('startBillingCycleId') as string, 10);
        let endBillingCycleId = parseInt(data.get('endBillingCycleId') as string, 10);

        if (startBillingCycleId === endBillingCycleId && defaultCycle) {
            startBillingCycleId = defaultCycle.id;
            endBillingCycleId = parseInt(defaultCycle.id as unknown as string, 10) + 2;
        }

        const submitInfo: ProjectStaff = {
            id: props.projectStaffInfo ? props.projectStaffInfo.id : 0,
            userId: props?.landman ? props?.landman.id : null,
            startDate: null,
            endDate: null,
            projectAdministratorId: null,
            projectManagerId: parseInt(data.get('projectManagerId') as string, 10) || null,
            pcapmId: parseInt(data.get('pcapmId') as string, 10) || null,
            isOverride: null,
            startBillingCycleId,
            endBillingCycleId,
            overrideBillRate: 0,
            overridePayRate: 0,
            ownershipAccess: (formRef.current.querySelector('[name="ownershipAccess"]') as HTMLInputElement)?.checked,
            contractAccess: (formRef.current.querySelector('[name="contractAccess"]') as HTMLInputElement)?.checked,
            reportAccess: (formRef.current.querySelector('[name="reportAccess"]') as HTMLInputElement)?.checked,
            gisAccess: (formRef.current.querySelector('[name="gisAccess"]') as HTMLInputElement)?.checked,
            workClassificationId: parseInt(data.get('workClassificationId') as string, 10) || null,
        };

        props.submitStaffingForm(submitInfo);
    };

    return (
        <>
            <Card badge={props.projectStaffInfo?.pendingCycle ? 'Pending new cycle' : ''} newContext={false}>
                <form ref={formRef}>
                    <div className="mb-1 mt-1">
                        <div id={'StaffingCardBody'} style={SLC_CARD_BODY}>
                            <div style={{ width: '120px' }}>
                                <label style={SLC_LABEL_NOWRAP}>{`${props.landman.firstName} ${props.landman.lastName}`}</label>
                            </div>
                            <div style={{ width: '160px' }}>
                                <div className="form-field" style={SLC_BODY_FLEX}>
                                    <select name="startBillingCycleId" style={SLC_FULL_WIDTH} defaultValue="">
                                        <option disabled={true} hidden={true} value="">-- Select --</option>
                                        {billingCycleOptions.map((o) => (
                                            <option key={`start-${o.value}`} value={o.value}>{o.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div style={{ width: '160px' }}>
                                <div className="form-field" style={SLC_BODY_FLEX}>
                                    <select name="endBillingCycleId" style={SLC_FULL_WIDTH} defaultValue="">
                                        <option disabled={true} hidden={true} value="">-- Select --</option>
                                        {billingCycleOptions.map((o) => (
                                            <option key={`end-${o.value}`} value={o.value}>{o.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div style={{ width: '150px' }}>
                                <div className="form-field" style={SLC_BODY_FLEX}>
                                    <select name="pcapmId" style={SLC_FULL_WIDTH} defaultValue={props.defaultPCAPM ?? ''}>
                                        <option disabled={true} hidden={true} value="">-- Select --</option>
                                        <option value="">None</option>
                                        {pcapmOptions.map((o) => (
                                            <option key={`pcapm-${o.value}`} value={o.value}>{o.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div style={{ width: '150px' }}>
                                <div className="form-field" style={SLC_BODY_FLEX}>
                                    <select name="workClassificationId" style={SLC_FULL_WIDTH} defaultValue={8}>
                                        <option disabled={true} hidden={true} value="">-- Select --</option>
                                        {workClassificationOptions.map((o) => (
                                            <option key={`wc-${o.value}`} value={o.value}>{o.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div style={{ width: '140px' }}>
                                <div className="form-field" style={SLC_BODY_FLEX}>
                                    <select name="projectManagerId" style={SLC_NOWRAP_FULL_WIDTH} defaultValue={props.defaultPM ?? ''}>
                                        <option disabled={true} hidden={true} value="">-- Select --</option>
                                        <option disabled={true} hidden={true} value="">-- Select --</option>
                                        <option value="">None</option>
                                        {projectManagerOptions.map((o) => (
                                            <option key={`pm-${o.value}`} value={o.value}>{o.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div style={SLC_CHECKBOX_ROW}>
                                <div className="form-field">
                                    <input type="checkbox" name="ownershipAccess" id={`ownership-${props.index}`} defaultChecked={true} style={SLC_NOWRAP} />
                                    <label htmlFor={`ownership-${props.index}`} style={SLC_NOWRAP}>Ownership</label>
                                </div>
                                <div className="form-field">
                                    <input type="checkbox" name="contractAccess" id={`contract-${props.index}`} defaultChecked={true} style={SLC_NOWRAP} />
                                    <label htmlFor={`contract-${props.index}`} style={SLC_NOWRAP}>Contracts</label>
                                </div>
                                <div className="form-field">
                                    <input type="checkbox" name="reportAccess" id={`report-${props.index}`} defaultChecked={true} style={SLC_NOWRAP} />
                                    <label htmlFor={`report-${props.index}`} style={SLC_NOWRAP}>Reports</label>
                                </div>
                                <div className="form-field">
                                    <input type="checkbox" name="gisAccess" id={`gis-${props.index}`} defaultChecked={true} style={SLC_NOWRAP} />
                                    <label htmlFor={`gis-${props.index}`} style={SLC_NOWRAP}>GIS</label>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </Card>
        </>
    );
};

export default React.memo(StaffingLandmanCard);



// WEBPACK FOOTER //
// ./src/components/project/sections/StaffingLandmanCard.tsx